import { createEnum } from '../helpers'

/**
 * The business timezone that defines CRT month boundaries. The server clock may be UTC, so "what
 * month is it right now" and the calendar-driven notification crons resolve against this tz.
 */
export const CRT_BUSINESS_TIMEZONE = 'America/Toronto'

/** Calendar-driven events: sent by the daily worker cron on a configurable day of the month. */
export const CRT_SCHEDULED_NOTIFICATION_EVENT_VALUES = ['missing_imports'] as const

/**
 * Events raised when a summary save changes status, mapped from the status in the backend's
 * `CRT_VERSION_STATUS_EVENTS` (`versions/transitions.ts`), with their email copy in the
 * worker's `SUMMARY_SAVE_TRANSITION_COPY`. None are enabled today.
 */
export const CRT_SUMMARY_SAVE_NOTIFICATION_EVENT_VALUES = [] as const

/** Events the CRT email notification system can raise. */
export const CRT_NOTIFICATION_EVENT_VALUES = [
  ...CRT_SCHEDULED_NOTIFICATION_EVENT_VALUES,
  ...CRT_SUMMARY_SAVE_NOTIFICATION_EVENT_VALUES,
] as const

export const CRT_NOTIFICATION_EVENT = createEnum(CRT_NOTIFICATION_EVENT_VALUES)

export type CrtNotificationEvent = (typeof CRT_NOTIFICATION_EVENT_VALUES)[number]

export type CrtScheduledNotificationEvent = (typeof CRT_SCHEDULED_NOTIFICATION_EVENT_VALUES)[number]

export type CrtSummarySaveNotificationEvent =
  (typeof CRT_SUMMARY_SAVE_NOTIFICATION_EVENT_VALUES)[number]

export function isCrtScheduledNotificationEvent(
  event: CrtNotificationEvent,
): event is CrtScheduledNotificationEvent {
  return (CRT_SCHEDULED_NOTIFICATION_EVENT_VALUES as readonly string[]).includes(event)
}

/** Send day used until an admin configures one. */
export const CRT_NOTIFICATION_DEFAULT_DAY_OF_MONTH: Record<CrtScheduledNotificationEvent, number> =
  {
    missing_imports: 7,
  }

/** Default send day of a scheduled event, null for a transition-driven one. */
export function getCrtNotificationDefaultDayOfMonth(event: CrtNotificationEvent): number | null {
  return isCrtScheduledNotificationEvent(event)
    ? CRT_NOTIFICATION_DEFAULT_DAY_OF_MONTH[event]
    : null
}

/** Capped at 28 so every month has the configured day. */
export const CRT_NOTIFICATION_MIN_DAY_OF_MONTH = 1
export const CRT_NOTIFICATION_MAX_DAY_OF_MONTH = 28

/** BullMQ job names on `QUEUE_CRT_NOTIFICATIONS`. */
export const CRT_NOTIFICATION_JOBS = {
  DAILY_SCHEDULE_CHECK: 'crt-notifications-daily-schedule-check',
  SUMMARY_SAVE_TRANSITION: 'crt-notifications-summary-save-transition',
} as const

/** Stable scheduler id so `upsertJobScheduler` replaces rather than duplicates on restart. */
export const CRT_NOTIFICATION_SCHEDULER_ID = 'crt-notifications-daily-schedule-check'

/** Per-event schedulers from before the send day was configurable; removed on worker boot. */
export const CRT_NOTIFICATION_LEGACY_SCHEDULER_IDS = [
  'crt-notifications-mope-started',
  'crt-notifications-missing-imports',
] as const

/** Payload enqueued by the backend when a summary save changes status. */
export type CrtSummarySaveTransitionPayload = {
  event: CrtSummarySaveNotificationEvent
  saveId: number
  saveName: string | null
  crtPeriod: string
  actorId: string
}
